import type { PedidoVenta } from '../services/pedidoVentaService';

export interface PedidoEstadoChipStyle {
  label: string;
  bgColor: string;
  textColor: string;
}

export const PEDIDO_ESTADOS = {
  NUEVO: 'Nuevo',
  IMPRESO: 'Impreso',
  LISTO_PICKEAR: 'Listo para pickear',
  PREPARADO: 'Preparado',
  FACTURADO: 'Facturado',
  ANULADO: 'Anulado',
} as const;

export const PEDIDO_ESTADO_CHIP: Record<string, PedidoEstadoChipStyle> = {
  [PEDIDO_ESTADOS.NUEVO]: { label: 'Nuevo', bgColor: '#FFF9C4', textColor: '#856404' },
  [PEDIDO_ESTADOS.IMPRESO]: { label: 'Impreso', bgColor: '#E3F2FD', textColor: '#1565C0' },
  [PEDIDO_ESTADOS.LISTO_PICKEAR]: {
    label: 'Listo para pickear',
    bgColor: '#BBDEFB',
    textColor: '#1565C0',
  },
  [PEDIDO_ESTADOS.PREPARADO]: { label: 'Preparado', bgColor: '#B3E5FC', textColor: '#01579B' },
  [PEDIDO_ESTADOS.FACTURADO]: { label: 'Facturado', bgColor: '#C8E6C9', textColor: '#2E7D32' },
  [PEDIDO_ESTADOS.ANULADO]: { label: 'Anulado', bgColor: '#FFCDD2', textColor: '#C62828' },
};

export const PEDIDO_ESTADO_FILTER_OPTIONS = [
  { value: PEDIDO_ESTADOS.NUEVO, label: PEDIDO_ESTADOS.NUEVO },
  { value: PEDIDO_ESTADOS.IMPRESO, label: PEDIDO_ESTADOS.IMPRESO },
  { value: PEDIDO_ESTADOS.LISTO_PICKEAR, label: PEDIDO_ESTADOS.LISTO_PICKEAR },
  { value: PEDIDO_ESTADOS.PREPARADO, label: PEDIDO_ESTADOS.PREPARADO },
  { value: PEDIDO_ESTADOS.FACTURADO, label: PEDIDO_ESTADOS.FACTURADO },
  { value: PEDIDO_ESTADOS.ANULADO, label: PEDIDO_ESTADOS.ANULADO },
];

/** Opciones de filtro en pendientes de facturación (sin estados cerrados). */
export const PEDIDO_ESTADO_PENDIENTES_FILTER_OPTIONS = PEDIDO_ESTADO_FILTER_OPTIONS.filter(
  (opt) => opt.value !== PEDIDO_ESTADOS.FACTURADO && opt.value !== PEDIDO_ESTADOS.ANULADO,
);

const KNOWN_ESTADOS = Object.keys(PEDIDO_ESTADO_CHIP);

/** Clave normalizada para comparar estados (minúsculas, sin acentos). */
export function estadoPedidoCompareKey(estado?: string | null): string {
  return (estado ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

const ESTADO_CANONICAL_BY_KEY: Record<string, string> = {
  ...Object.fromEntries(KNOWN_ESTADOS.map((s) => [estadoPedidoCompareKey(s), s])),
  'listo para picking': PEDIDO_ESTADOS.LISTO_PICKEAR,
  'listo para picker': PEDIDO_ESTADOS.LISTO_PICKEAR,
  'listo para pick': PEDIDO_ESTADOS.LISTO_PICKEAR,
  'listo picking': PEDIDO_ESTADOS.LISTO_PICKEAR,
  'en picking': PEDIDO_ESTADOS.LISTO_PICKEAR,
  'listo para pickear': PEDIDO_ESTADOS.LISTO_PICKEAR,
};

function pickFirstNonEmptyString(values: unknown[]): string | undefined {
  for (const val of values) {
    if (val != null && String(val).trim() !== '') {
      return String(val).trim();
    }
  }
  return undefined;
}

function nestedEstado(obj: unknown): string | undefined {
  if (!obj || typeof obj !== 'object') return undefined;
  const o = obj as Record<string, unknown>;
  return pickFirstNonEmptyString([o.estado, o.Estado]);
}

/** Lee estado crudo desde un registro de pedido/formulario (sin normalizar). */
export function estadoDesdeRegistroPedido(raw: Record<string, unknown>): string | undefined {
  const formulario = raw.formulario ?? raw.Formulario;
  const fromFields = pickFirstNonEmptyString([
    raw.estado,
    raw.Estado,
    raw.estadoMaster,
    raw.EstadoMaster,
    raw.estadoComprobanteMaster,
    raw.EstadoComprobanteMaster,
    raw.estadoFormularioMaster,
    raw.EstadoFormularioMaster,
    raw.estadoPedido,
    raw.EstadoPedido,
    nestedEstado(formulario),
  ]);
  if (fromFields) return fromFields;

  const asentado = String(raw.asentado ?? raw.Asentado ?? '').trim().toUpperCase();
  if (asentado === 'P' || asentado === 'K') return PEDIDO_ESTADOS.LISTO_PICKEAR;
  if (asentado === 'I') return PEDIDO_ESTADOS.IMPRESO;

  return undefined;
}

export function estadoDesdePedido(pedido: PedidoVenta | Record<string, unknown>): string | undefined {
  return estadoDesdeRegistroPedido(pedido as Record<string, unknown>);
}

/** Normaliza estado del pedido al valor canónico usado en filtros y chips. */
export function normalizeEstadoPedidoMaster(estado?: string | null): string {
  const key = estadoPedidoCompareKey(estado);
  if (!key) return PEDIDO_ESTADOS.NUEVO;
  return ESTADO_CANONICAL_BY_KEY[key] ?? ((estado ?? '').trim() || PEDIDO_ESTADOS.NUEVO);
}

export function estadosPedidoCoinciden(a?: string | null, b?: string | null): boolean {
  return normalizeEstadoPedidoMaster(a) === normalizeEstadoPedidoMaster(b);
}

export function getPedidoEstadoChip(estado?: string | null): PedidoEstadoChipStyle {
  const key = normalizeEstadoPedidoMaster(estado);
  return PEDIDO_ESTADO_CHIP[key] ?? { label: key, bgColor: '#FFF9C4', textColor: '#856404' };
}
